import React, {useState, useEffect} from "react";
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  CircularProgress,
} from "@material-ui/core";
import Expando from "routes/workflow/workflow_detail/components/expando";
import ConfigurationInputs from "./configuration_inputs";

import {Definition} from "./data_assets_create_view";
import {
  WorkflowInstance,
  WorkflowInputValues,
  WorkflowInputValue,
} from "common/flowmaster/types/workflow";

const styles = {
  sectionLabel: {
    display: "flex",
    alignItems: "center",
    height: "48px",
    fontWeight: "bold",
  },
  formControl: {
    width: "100%",
    maxWidth: 500,
    marginTop: "1em",
  },
  helperText: {
    color: "#0000008A",
    fontSize: "0.75rem",
    marginTop: ".5em",
  },
  errorText: {
    color: "#f44336",
    fontSize: "0.75rem",
  },
  container: {
    border: "1px solid #ddd",
    borderRadius: 4,
    padding: "1em",
    display: "flex",
    flexDirection: "column",
    gap: "1em",
    marginBottom: "1em",
  },
} as const;

interface ConfigurationSectionProps {
  selectedDefinition?: Definition;
  selectedConfigId: number | string;
  configurations: WorkflowInstance[];
  configExpanded: boolean;
  isLoadingConfigs: boolean;
  fetchConfigsError: string | null;
  basePath: string;
  push: (path: string) => void;
  handleConfigChange: (event: React.ChangeEvent<{value: unknown}>) => void;
}

const ConfigurationSection: React.FC<ConfigurationSectionProps> = ({
  selectedDefinition,
  selectedConfigId,
  configurations,
  configExpanded,
  isLoadingConfigs,
  fetchConfigsError,
  handleConfigChange,
}) => {
  const [inputValues, setInputValues] = useState<WorkflowInputValues>({});

  const selectedConfig = configurations.find(
    config => config.id === Number(selectedConfigId),
  );

  useEffect(() => {
    if (selectedConfig?.config?.inputs?.values) {
      setInputValues({...selectedConfig.config.inputs.values});
    } else {
      setInputValues({});
    }
  }, [selectedConfig]);

  const handleInputChange = (inputId: string, value: WorkflowInputValue) => {
    setInputValues(prev => ({
      ...prev,
      [inputId]: value,
    }));
  };

  const configHeader = (
    <div style={{display: "flex", alignItems: "center"}}>
      <Typography variant="body1" style={styles.sectionLabel}>
        Configuration
      </Typography>
      {isLoadingConfigs && (
        <CircularProgress size={20} style={{marginLeft: "1em"}} />
      )}
    </div>
  );

  const configContent = (
    <div style={{width: "100%", display: "flex", flexDirection: "column"}}>
      <div style={{display: "flex", alignItems: "center"}}>
        <Typography variant="body1" style={styles.sectionLabel}>
          Configuration
        </Typography>
        {isLoadingConfigs && (
          <CircularProgress size={20} style={{marginLeft: "1em"}} />
        )}
      </div>

      {fetchConfigsError ? (
        <Typography variant="body2" style={styles.errorText}>
          Error: {fetchConfigsError}
        </Typography>
      ) : (
        !isLoadingConfigs && (
          <>
            <FormControl variant="outlined" style={styles.formControl}>
              <InputLabel id="config-selector-label">
                Select Configuration
              </InputLabel>
              <Select
                value={selectedConfigId}
                onChange={handleConfigChange}
                label="Select Configuration"
                disabled={!selectedDefinition || configurations.length === 0}
              >
                <MenuItem value="">
                  <em>-- Select a configuration --</em>
                </MenuItem>
                {configurations.map(config => (
                  <MenuItem key={config.id} value={config.id}>
                    {config.name}
                  </MenuItem>
                ))}
              </Select>
              {!selectedDefinition && (
                <Typography variant="body2" style={styles.helperText}>
                  Please select a definition first.
                </Typography>
              )}

              {selectedDefinition && configurations.length === 0 && (
                <Typography variant="body2" style={styles.helperText}>
                  No configurations available for this definition.
                </Typography>
              )}
            </FormControl>

            {selectedDefinition && selectedConfig && (
              <ConfigurationInputs
                definition={selectedDefinition}
                inputValues={inputValues}
                onInputChange={handleInputChange}
              />
            )}
          </>
        )
      )}
    </div>
  );

  return (
    <div style={styles.container}>
      <Expando beginExpanded={configExpanded}>
        {configHeader}
        {configContent}
      </Expando>
    </div>
  );
};

export default ConfigurationSection;
